// src/pages/DeliveryNotes/DnPrintTemplate.jsx
import React from "react";
import "./DnPrintTemplate.css";

export default function DnPrintTemplate({
  dn,
  so,
  items,
  mode,
  copyType = "CUSTOMER'S COPY",
  showOverrideNote = true
}) {
  if (!dn || !so) return null;

  // ✅ GROUP: same CatalogNumber + same LotNumber => sum QtyDelivered
  const groupedItems = (() => {
    const map = new Map();

    (items || []).forEach((it) => {
      const catalog = it.CatalogNumber || "";
      const lot = it.LotNumber || "";
      const pack = it.Packing || "";
      const desc = it.Description || "";
      const expiryKey = it.ExpiryDate
        ? new Date(it.ExpiryDate).toISOString().slice(0, 10)
        : "";

      const key = `${catalog}||${lot}||${pack}||${desc}||${expiryKey}`;

      const qty = Number(it.QtyDelivered ?? 0) || 0;

      if (!map.has(key)) {
        map.set(key, {
          ...it,
          QtyDelivered: qty
        });
      } else {
        const prev = map.get(key);
        prev.QtyDelivered = (Number(prev.QtyDelivered) || 0) + qty;

        // keep earliest expiry if ever mixed (just in case)
        if (it.ExpiryDate) {
          const prevDate = prev.ExpiryDate ? new Date(prev.ExpiryDate) : null;
          const newDate = new Date(it.ExpiryDate);
          if (!prevDate || newDate < prevDate) prev.ExpiryDate = it.ExpiryDate;
        }

        map.set(key, prev);
      }
    });

    return Array.from(map.values());
  })();

  return (
    <div className="dn-print">
      {/* HEADER */}
      <div className="dn-header-row">
        <img
          src="/img/biocare-logo.png"
          alt="Biocare Logo"
          className="dn-logo"
        />

        <div className="dn-company">
          <strong>BIOCARE, INC.</strong>
          <br />
          Room 306 WEBJET Bldg., 64 Quezon Avenue,
          <br />
          B.M.A. Ave., Tatalon, Quezon City
          <br />
          Telephone: 8732-0535, 8712-0531
          <br />
          8714-9159, 8712-5389
          <br />
          Fax: (02) 8711-4310
          <br />
          VAT REG. TIN: 006-163-782-000
        </div>

        {/* DN NUMBER */}
        <div className="dn-header-right">
          <div className="dn-ref-label">REFER TO THIS NUMBER</div>
          <table className="dn-num-table">
            <thead>
              <tr>
                <th>DAY</th>
                <th>MO</th>
                <th>YR</th>
                <th>NUMBER</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>{dn.DNDate?.slice?.(8, 10) || ""}</td>
                <td>{dn.DNDate?.slice?.(5, 7) || ""}</td>
                <td>{dn.DNDate?.slice?.(2, 4) || ""}</td>
                <td className="dn-number">{dn.DNNumber}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* TITLE */}
      <div className="dn-title">DELIVERY NOTE</div>

      {/* CONTACT ROW */}
      <div className="dn-contact-row">
        <div className="contact-col">
          <div className="label">CUSTOMER</div>
          <div className="value">{so.CustomerName}</div>
        </div>

        <div className="contact-col">
          <div className="label">SHIP TO</div>
          <div className="value">{so.ShipTo}</div>
        </div>

        <div className="contact-col">
          <div className="label">MARK TO</div>
          <div className="value">{so.MarkTo || ""}</div>
        </div>
      </div>

      {/* CUSTOMER INFO */}
      <table className="dn-customer-table">
        <tbody>
          <tr>
            <td>
              <div className="label">CUSTOMER NUMBER</div>
              <div className="value">{so.CustomerNumber || ""}</div>
            </td>

            <td>
              <div className="label">AREA CODE</div>
              <div className="value">{so.AreaManagerCode || ""}</div>
            </td>

            <td>
              <div className="label">REFERENCE ORDER</div>
              <div className="value">{so.ReferenceOrder || so.SONumber}</div>
            </td>

            <td>
              <div className="label">DATE</div>
              <div className="value">
                {dn.DNDate ? new Date(dn.DNDate).toLocaleDateString("en-PH") : ""}
              </div>
            </td>

            <td>
              <div className="label">SHIPPING INSTRUCTION</div>
              <div className="value">{so.ShippingInstruction || ""}</div>
            </td>
          </tr>
        </tbody>
      </table>

      {/* SPECIAL INSTRUCTION */}
      <div className="dn-special">
        <div className="label">SPECIAL INSTRUCTION</div>
        <div className="value">
          {dn.SpecialInstruction || dn.special_instruction || "-"}
        </div>
      </div>

      {/* ITEM TABLE */}
      <table className="dn-item-table">
        <thead>
          <tr>
            <th>QUANTITY</th>
            <th>UNIT OF MEASURE</th>
            <th>CATALOG NUMBER</th>
            <th>ITEM AND DESCRIPTION</th>
            <th>LOT NO</th>
            <th>EXPIRY DATE</th>
          </tr>
        </thead>

        <tbody>
          {groupedItems.length === 0 && (
            <tr>
              <td colSpan="6" style={{ textAlign: "center" }}>
                NO ITEMS DELIVERED
              </td>
            </tr>
          )}

          {groupedItems.map((it, idx) => (
            <tr key={idx}>
              <td>{it.QtyDelivered}</td>
              <td>{it.Packing}</td>
              <td>{it.CatalogNumber}</td>
              <td>{it.Description}</td>

              {/* ✅ LOT (QTY) */}
              <td>
                {it.LotNumber}
                {Number(it.QtyDelivered) > 1 ? ` (${it.QtyDelivered})` : ""}
              </td>

              <td>
                {it.ExpiryDate
                  ? new Date(it.ExpiryDate).toLocaleDateString("en-PH")
                  : ""}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* FOOTER */}
      <div className="dn-footer">
        {showOverrideNote && mode === "EDITED" && (
          <div className="dn-override-note">
            This is a PRINT OVERRIDE copy for customer use. Accounting and stock
            records remain based on the ORIGINAL delivery note.
          </div>
        )}

        <div className="dn-disclaimer">
          ANY LOSS OR DAMAGE DUE TO LEAKAGE, BREAKAGE, OR UNDETERMINED CAUSE AFTER
          DELIVERY OF SHIPMENT TO FORWARDER IS NOT OUR RESPONSIBILITY. SHIPMENT OF
          GOODS IS AT BUYER'S RISK AND COST. GOODS REMAIN AS THE PROPERTY OF THE
          COMPANY UNTIL FULLY PAID.
        </div><br></br>

        {/* PREPARED / CHECKED / PACKED */}
        {/* <div className="dn-prepared-block">
          <div className="dn-prepared-line">PREPARED BY __________________</div>
          <div className="dn-prepared-line">CHECKED BY ___________________</div>
          <div className="dn-prepared-line">PACKED BY _____________________</div>
        </div> */}


        <div className="dn-footer-row">
          <div className="dn-footer-left">
            <div className="f-line">
              <span>TRANSFER TO:</span>
              <span className="line"></span>
            </div>

            <div className="f-line">
              <span>INVOICE NO.</span>
              <span className="line"></span>
            </div>

            <div className="f-line">
              <span>DATE</span>
              <span className="line"></span>
            </div>
          </div>

          <div className="dn-footer-right">
            <div className="received-title">
              MERCHANDISE SPECIFIED ABOVE
              <br />
              WERE RECEIVED IN GOOD CONDITION.
            </div>

            <div className="signature-lines">
              <div className="line-long"></div>
              <div className="line-long"></div>
            </div>

            <div className="signature-labels">
              <span>PRINT NAME</span>
              <span>SIGNATURE</span>
            </div>

            <div className="date-group">
              <div className="date-line"></div>
              <div className="date-label">DATE</div>
            </div>
          </div>
        </div>
        <div className="dn-copy-label">
          {copyType}
        </div>
      </div>
    </div>
  );
}
